import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { invoke } from '@tauri-apps/api/core';
import { LogIn } from 'lucide-react';
import { isTauri } from '../utils/tauri';
import { subscribeTauriEvent } from '../utils/safeTauriEvent';
import { useAuthStore } from '../store/authStore';
import './Vault.css';

const RECHECK_MS = 60 * 1000;

/**
 * This computer changed the master passphrase, but the server has not taken
 * the new vault key yet (code review of #475). Reads
 * `vault_sync_status.rekeyPush`:
 *
 * - `"reauth"`: the sign-in is no longer recent enough for a key change. Sign
 *   in again; the waiting change is sent right after. Until then, nothing
 *   from this vault syncs.
 * - `"rekeyed"`: another device changed the passphrase at the same moment
 *   and its change was kept. Its key arrives by sync; the "changed on another
 *   device" banner then asks for that passphrase.
 */
export default function VaultRekeyPushBanner() {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const read = useCallback(async () => {
    try {
      setStatus(await invoke('vault_sync_status'));
    } catch {
      // Keep the last answer.
    }
  }, []);

  useEffect(() => {
    if (!isTauri()) return undefined;
    read();
    const timer = setInterval(read, RECHECK_MS);
    const subs = ['vault:changed', 'vault:master-changed'].map((e) => subscribeTauriEvent(e, read));
    return () => {
      clearInterval(timer);
      subs.forEach((s) => s?.unlisten?.());
    };
  }, [read]);

  const signIn = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const result = await useAuthStore.getState().loginWithBrowser();
      if (result?.success && status?.scopeId) {
        await invoke('sync_pull_now', { workspaceId: status.scopeId }).catch(() => {});
      }
    } finally {
      setBusy(false);
      read();
    }
  };

  const state = status?.rekeyPush;
  if (!isTauri() || (state !== 'reauth' && state !== 'rekeyed')) return null;

  if (state === 'rekeyed') {
    return (
      <div className="vault-pass-banner" data-testid="vault-rekey-push-rekeyed">
        <div className="vault-pass-banner-texts">
          <span className="vault-pass-banner-title">
            {t('vault.rekeyPushRaceTitle', 'Your passphrase was also changed on another device')}
          </span>
          <span className="vault-pass-banner-text">
            {t(
              'vault.rekeyPushRaceBody',
              'That change was kept. When it reaches this computer, you will be asked for the passphrase set there. Nothing on this computer is lost meanwhile.'
            )}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="vault-pass-banner vault-pass-banner--warn" data-testid="vault-rekey-push-reauth">
      <LogIn size={15} className="vault-pass-banner-icon" />
      <div className="vault-pass-banner-texts">
        <span className="vault-pass-banner-title">
          {t('vault.rekeyPushReauthTitle', 'Sign in again to finish changing your passphrase')}
        </span>
        <span className="vault-pass-banner-text">
          {t(
            'vault.rekeyPushReauthBody',
            'The new passphrase works on this computer already, but your other devices do not have it yet, and vault changes wait until you sign in again.'
          )}
        </span>
      </div>
      <div className="vault-pass-banner-actions">
        <button
          type="button"
          className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors modern-button text-sm"
          onClick={signIn}
          disabled={busy}
        >
          {busy ? t('vault.rekeyPushReauthBusy', 'Signing in…') : t('vault.rekeyPushReauthAction', 'Sign in')}
        </button>
      </div>
    </div>
  );
}
